export default customElements.define('w-tab-panel-menu', class PageTabs extends wealthify.CustomElementBaseClass {
    constructor () {
        const styleSelector = 'c-tab-panel-menu';
        const config = {
            styleSelector: styleSelector,
            templateHTML: /*html*/`
            <nav role='navigation' aria-label='Sub navigation' data-root>
              <div class='${styleSelector}__container u-display__none--mobile' data-tab-container role='tablist'>
              </div>
              <w-field-select hidetablet='true' hidedesktop='true' label='Select a category' data-select></w-field-select>
            </nav>`,
            cacheDom: ['data-tab-container', 'data-select']
        };

        super(config);

        this.onConnectedCallback = () => {
            this.addEventListener(this.events.SelectTab.name, (event) => {
                this.dom.select.value = event.detail.selected;
            });
    
            this.dom.select.addEventListener(this.dom.select.events.ValueChange.name, (event) => {
                this.selected = event.target.value;
            });
        };
    }

    static eventSettings = Object.freeze({events: {SelectTab: 'select-tab'}});

    static get validTabProps () {
        // The panelId tab prop relates to the panel's DOM element id attribute.
        // This intentionally goes against the convention of using data attributes for element fetching.
        // In this specific case id's are required to link up aria-labelledby.
        return ['name', 'panelId'];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('tabs', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.ArrayObjectContainsKeys(this.validTabProps)],
                required: true
            }),
            new wealthify.Prop('selected', {
                type: wealthify.propTypes.Number(),
                default: 0
            }),
            new wealthify.Prop('unread', {type: wealthify.propTypes.UniqueArray()})
        ];
    }

    get propCallbacks () {
        return {
            tabs: () => {
                this._buildTabs();
                this._populateSelectBox();
            },
            selected: () => this._setSelected(this.selected),
            unread: () => this._setUnread()
        };
    }

    // Private

    #tabsBuilt = false;
    #maxNumberOfTabsBeforeCollapse = 2;

    _populateSelectBox(){     
        this.dom.select.options = this.tabs.map((tab, index) => {
            return ({
                label: tab.name,
                value: index.toString()
            });
        });

        this.dom.select.value = this.selected.toString();
    }

    _buildTabs() {      
        this.dom.tabButtons = [];
        this.dom.tabUnreadNotifications = [];
        this.dom.tabPanels = [];
        
        const tabPanelIdsAreUnique = wealthify.type.array.isUniqueArray(this.tabs.map(tab => tab.panelId));
        
        // Ensure no duplicate panel ids are passed to the component
        if(!tabPanelIdsAreUnique) {
            wealthify.error.throwError(this.tagName, 'The panelIds properties of the tabs prop must be unique.');
        } 
        
        this.tabs.forEach((tab, index) => {
            const isSinglePanelPresent = document.querySelectorAll(`#${tab.panelId}`).length === 1;
          
            // Ensure only one content panel exists per tab
            if(!isSinglePanelPresent) {
                wealthify.error.throwError(this.tagName, `The required '${tab.panelId}' element should appear in the DOM once.`);
            }

            const buttonId = `${tab.panelId}-tab`;
            const button = document.createElement('button');
            
            const tabPanel = this.tabs[index].panel = document.querySelector(`#${tab.panelId}`);

            tabPanel.setAttribute('aria-labelledby', buttonId);
            
            button.classList.add(`${this.styleSelector}__tab`);
            button.type = 'button';
            button.role = 'tab';
            button.id = buttonId;
            button.setAttribute('aria-controls', tab.panelId);
            
            button.addEventListener('click', () => {
                this._setSelected(index);
                
                this.events.SelectTab.dispatch({
                    selected: index,
                    tab: button,
                    panel: tabPanel
                });
            });

            button.addEventListener('keydown', event => {
                if(event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
              
                const prospectiveIndex = event.key === 'ArrowLeft' ? index - 1 : index + 1;
                const selectableIndex = this.tabs[prospectiveIndex] ? prospectiveIndex : index;
                
                this.dom.tabButtons[selectableIndex].focus();
            });
            
            const spanTag = document.createElement('span');

            spanTag.innerHTML = tab.name;
            spanTag.classList.add(`${this.styleSelector}__tab-text`);
            
            button.appendChild(spanTag);
            
            const tabUnreadNotification = document.createElement('span');
                
            tabUnreadNotification.role = 'status';
            tabUnreadNotification.classList.add(`${this.styleSelector}__tab-unread`);
            tabUnreadNotification.classList.add('u-display__none');
            tabUnreadNotification.setAttribute('aria-label', 'Unread');
              
            button.appendChild(tabUnreadNotification);

            this.dom.tabPanels.push(tabPanel);
            this.dom.tabButtons.push(button);
            this.dom.tabUnreadNotifications.push(tabUnreadNotification);
            this.dom.tabContainer.appendChild(button);
        });

        this.#tabsBuilt = true;
        this._toggleCollapsable(this.tabs.length > this.#maxNumberOfTabsBeforeCollapse);
    }

    _toggleCollapsable(shouldCollapse){
        const tabsMobileClassListAction = shouldCollapse ? 'add' : 'remove';
        const selectClassListAction = shouldCollapse ? 'remove' : 'add';

        this.dom.tabContainer.classList[tabsMobileClassListAction]('u-display__none--mobile');
        this.dom.select.classList[selectClassListAction]('u-display__none');
    }

    _setSelected(value) {      
        if(!this.#tabsBuilt) return;

        this.dom.tabButtons.forEach((button, index) => {          
            const isButtonSelected = index === value;            
            const buttonClassListAction = isButtonSelected ? 'add' : 'remove';

            button.setAttribute('aria-selected', isButtonSelected);
            button.classList[buttonClassListAction](`${this.styleSelector}__tab--selected`);

            const panelClassListAction = !isButtonSelected ? 'add' : 'remove';
            const relatedTabPanel = this.tabs[index].panel;

            relatedTabPanel.classList[panelClassListAction]('u-display__none');
        });
    }

    _setUnread() {      
        if(!this.#tabsBuilt) return;

        this.dom.tabUnreadNotifications.forEach((unreadNotification, index) => {
            const classListAction = !this.unread.includes(index) ? 'add' : 'remove';

            unreadNotification.classList[classListAction]('u-display__none');
        });
    }
});